import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/taskUi";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Flag, CheckCircle2, Circle, Trash2, Pencil, Plus, GripVertical, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function MilestoneRow({ m, canManage, onToggle, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id, disabled: !canManage });
  const done = m.status === "completed";
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`milestone-${m.id}`}>
      {canManage && <button type="button" {...attributes} {...listeners} className="cursor-grab touch-none text-slate-300 hover:text-slate-500" aria-label="Drag to reorder" data-testid={`milestone-drag-${m.id}`}><GripVertical className="w-4 h-4" /></button>}
      {canManage ? (
        <button onClick={() => onToggle(m)} title={done ? "Reopen" : "Mark complete"} data-testid={`milestone-toggle-${m.id}`}>
          {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-brand" />}
        </button>
      ) : <span data-testid={`milestone-status-${m.id}`}>{done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}</span>}
      <Flag className="w-4 h-4 text-brand shrink-0" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${done ? "line-through text-slate-400" : "text-slate-800"}`} data-testid={`milestone-title-${m.id}`}>{m.title}</p>
        {m.description && <p className="text-xs text-slate-500 truncate">{m.description}</p>}
        <p className="text-xs text-slate-400">{m.task_completed}/{m.task_total} tasks{m.owner_name ? ` · ${m.owner_name}` : ""}</p>
      </div>
      <span className="text-xs text-slate-500 whitespace-nowrap">{m.due_date ? fmtDate(m.due_date) : "No date"}</span>
      {done && canManage && <button onClick={() => onToggle(m)} className="text-xs text-slate-500 hover:text-brand flex items-center gap-1" data-testid={`milestone-reopen-${m.id}`}><RotateCcw className="w-3.5 h-3.5" />Reopen</button>}
      {canManage && <button onClick={() => onEdit(m)} className="text-slate-300 hover:text-brand" title="Edit" data-testid={`milestone-edit-${m.id}`}><Pencil className="w-4 h-4" /></button>}
      {canManage && <button onClick={() => onDelete(m)} className="text-slate-300 hover:text-rose-500" title="Delete" data-testid={`milestone-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

function MilestoneDialog({ projectId, milestone, onClose, onDone }) {
  const [f, setF] = useState({ title: milestone?.title || "", description: milestone?.description || "", due_date: (milestone?.due_date || "").slice(0, 10), completed: milestone?.status === "completed" });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Milestone name is required");
    const body = { title: f.title.trim(), description: f.description || null, due_date: f.due_date || null };
    try {
      if (milestone) await api.patch(`/projects/${projectId}/milestones/${milestone.id}`, { ...body, status: f.completed ? "completed" : "open" });
      else await api.post(`/projects/${projectId}/milestones`, body);
      toast.success(milestone ? "Milestone updated" : "Milestone added"); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="milestone-dialog">
        <DialogHeader><DialogTitle>{milestone ? "Edit Milestone" : "Add Milestone"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Name *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Insurance Licensing Completed" data-testid="milestone-title" /></div>
          <div><Label className="text-xs font-semibold">Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="milestone-description" /></div>
          <div><Label className="text-xs font-semibold">Target date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="milestone-due" /></div>
          {milestone && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.completed} onChange={(e) => setF({ ...f, completed: e.target.checked })} data-testid="milestone-completed" />Completed</label>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" onClick={save} data-testid="milestone-submit">{milestone ? "Save Changes" : "Add"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MilestonesPanel({ projectId, milestones, canManage, onChanged }) {
  const [items, setItems] = useState(milestones);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { setItems(milestones); }, [milestones]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const toggle = (m) => api.patch(`/projects/${projectId}/milestones/${m.id}`, { status: m.status === "completed" ? "open" : "completed" })
    .then(() => { toast.success(m.status === "completed" ? "Milestone reopened" : "Milestone completed"); onChanged(); }).catch((e) => toast.error(apiError(e)));
  const del = async (m) => {
    const note = m.task_total ? ` Its ${m.task_total} task(s) stay in the project with no milestone.` : "";
    if (!window.confirm(`Delete milestone "${m.title}"?${note}`)) return;
    try { await api.delete(`/projects/${projectId}/milestones/${m.id}`); toast.success("Milestone deleted"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const end = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((m) => m.id);
    const next = arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id));
    setItems(next);
    try { await api.post(`/projects/${projectId}/milestones/reorder`, { ids: next.map((m) => m.id) }); toast.success("Milestone order saved"); }
    catch (e) { toast.error(apiError(e)); onChanged(); }
  };
  return (
    <div data-testid="project-milestones">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400">{canManage && items.length > 1 ? "Drag ☰ to change the order." : ""}</p>
        {canManage ? <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => setDialog({})} data-testid="project-add-milestone"><Plus className="w-4 h-4 mr-1" />Add Milestone</Button>
          : <p className="text-xs text-slate-400" data-testid="milestones-readonly-note">Milestones are managed by Project Editors.</p>}
      </div>
      {items.length ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
          <SortableContext items={items.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">{items.map((m) => <MilestoneRow key={m.id} m={m} canManage={canManage} onToggle={toggle} onEdit={(x) => setDialog({ m: x })} onDelete={del} />)}</div>
          </SortableContext>
        </DndContext>
      ) : <EmptyState icon={Flag} title="No milestones" description="Break the project into milestones." />}
      {dialog && <MilestoneDialog projectId={projectId} milestone={dialog.m} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged(); }} />}
    </div>
  );
}
